/**
 * Determine whether a mapping option should be disabled.
 *
 * @param {boolean} isAnalogInput - Whether the source is an analog input (allows numeric targets).
 * @param {string} key - Identifier of the target point to check.
 * @param {{type?: string, readOnly?: boolean}} item - Metadata for the target point (e.g. type, readOnly).
 * @param {Object<string, any>} mappedPoints - Map of already-mapped points; presence of key disables the option.
 * @returns {boolean} True if the option should be disabled, false otherwise.
 */
const getOptionDisabledStatus = (isAnalogInput, key, item, mappedPoints, mappedObject) => {
  if(mappedPoints[key]){
    return true;
  }
  if(isAnalogInput){
    return false; // if it's an analog input, allow mapping to numerical points.
  }
  if(item.type === 'number' && !item.readOnly){
    return true; // if it's not an analog input, disable mapping to writable numerical points.
  }

  const hasEnum =  mappedObject.customEnum && Object.keys(mappedObject.customEnum).length > 0;
  if(item.type === 'enumerated' && !hasEnum){
    return true; // if it's an enumerated type but there's no custom enum defined, disable it.
  }

  return false;
};

/**
 * Build a lookup of property names found in a template's data items.
 *
 * @param {Object} template - Template object that may contain a `data` map.
 * @param {Object.<string, {property?: string}>} [template.data={}] - Map of items; each item may have a `property` string.
 * @returns {Object.<string, boolean>} Map where each discovered property name is a key with value `true`.
 */
const getMappedPoints = (template) => {
  const {data = {}} = template;
  const map = {};
  Object.values(data).forEach(item => {
    if(item.property){
      map[item.property] = true;
    }
  });
  return map;
};


/**
 * Parse universal entity property definitions into UI select options grouped by entity type.
 *
 * Converts the provided universalEntityPropertiesByEntityType object into a `mainOptions` object
 * where each key is an entity type and the value is an array of option objects:
 * `{ text: string, value: string, disabled: boolean }`.
 * If `universalEntityPropertiesByEntityType` is falsy, returns `null`.
 *
 * @param {Object} template - Template object (passed to internal helpers like getMappedPoints).
 * @param {Object<string, Object<string, {text: string}>>|null} universalEntityPropertiesByEntityType
 *   Mapping of entityType => (mappingKey => property definition). Each property should include a `text` field.
 * @param {Object} mappedObject - The mapped object metadata. Its `type` field is used to detect analog input (analog if `type === 5`).
 * @returns {{ mainOptions: Object<string, Array<{text: string, value: string, disabled: boolean}>> } | null}
 *   An object containing grouped select options under `mainOptions`, or `null` when input is not provided.
 */
export const getParsedUniversalOptions = (
  template,
  universalEntityPropertiesByEntityType,
  mappedObject
) => {
  if (!universalEntityPropertiesByEntityType) {
    return null;
  }

  const mappedPoints = getMappedPoints(template);
  const isAnalogInput = mappedObject.type === 5;

  const mainOptions = {};
  Object.entries(universalEntityPropertiesByEntityType).forEach(
    ([type, typeOptions]) => {
      const parsedTypeOptions = Object.entries(typeOptions).map(
        ([key, item]) => {

          const option = {
            text: item.text,
            value: key,
            disabled: getOptionDisabledStatus(isAnalogInput, key, item, mappedPoints, mappedObject), // if it's an analog input, allow mapping to numerical points.
          };
          return option;
        }
      );
      mainOptions[type] = parsedTypeOptions;
    }
  );

  return { mainOptions };
};
